import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {HStack, VStack} from "native-base";
import {useToast} from "react-native-toast-notifications";
import {t} from "../../../../../../../services/i18n";
import {Colors} from "../../../../../../../styles/Colors";
import {GlobalStyleAttributes} from "../../../../../../../styles/GlobalStyles";
import AppText from "../../../../../../common/AppText";
import CustomButtonV2 from "../../../../../../common/CustomButtonV2";
import ConfirmModal from "../../../../../../common/ConfirmModal";
import AutoMessages from "./AutoMessages";
import {FLOW_MANAGER_MODAL, openModal} from "../../../../../../../redux/modalManagerSlice";
import {modalFlowOptions} from "../../../../../Popups/ModalFlow/ModalFlowOptions";
import {autoMessageTypes} from "../../SettingsConfig";
import {fetchApi} from "../../../../../../../services/HTTP";
import {getAutoActions} from "../../../../../../../redux/boxSlice";

const AutoMessagesGroup = (props) => {
    const {
        messageType,
        sessionType = null,
        filterRow,
        groupLabel,
        header,
        subheader,
        periodAmountTextBefore,
        periodAmountTextAfter,
    } = props;
    const dispatch = useDispatch();
    const toast = useToast();
    const autoActions = useSelector(state => state.box.box.auto_actions);
    const [deleteTemplateId, setDeleteTemplateId] = useState(null);

    const groupRows = useMemo(() => {
        if (!autoActions?.length) return [];
        const matchRow = filterRow ?? ((item) =>
            item.auto_actions?.action === messageType && item.session_type === sessionType
        );
        return autoActions.filter(matchRow);
    }, [autoActions, messageType, sessionType, filterRow]);

    const templateIds = useMemo(() => {
        const ids = new Set();
        groupRows.forEach(item => {
            if (item.message_template?.id) ids.add(item.message_template.id);
        });
        return [...ids].sort((a, b) => a - b);
    }, [groupRows]);

    const cards = templateIds.length ? templateIds : [null];

    const openNewCardModal = () => {
        dispatch(openModal({
            modalName: FLOW_MANAGER_MODAL,
            props: {
                initSelection: modalFlowOptions.SESSION_AUTO_MESSAGE,
                initProps: {
                    messageType,
                    periodAmountTextBefore,
                    periodAmountTextAfter,
                    sessionType,
                    messageTemplateId: null,
                },
                target: [],
            },
        }));
    };

    const deleteCard = async () => {
        try {
            const isBefore = messageType === autoMessageTypes.BEFORE_TRIAL_CLASS
            const isAfter = messageType === autoMessageTypes.AFTER_TRIAL_CLASS
            await fetchApi('autoActionsSet/deleteSessionAutoMessage', 'post', {
                actionName: (isBefore || isAfter) ? 'LEAD_ATTENDANCE' : messageType,
                session_type: sessionType,
                message_template_id: deleteTemplateId,
                ...(isBefore && {before_after: 'BEFORE'}),
                ...(isAfter && {before_after: 'AFTER'}),
            }, false, true)
            dispatch(getAutoActions())
            toast.show(t('savedSuccessfully'), {type: 'success', duration: 2000})
        } catch (error) {
            toast.show(t('somethingWentWrongPleaseTryAgain'), {type: 'danger', duration: 4000})
        } finally {
            setDeleteTemplateId(null)
        }
    }

    return (
        <VStack space={'16px'}>
            {groupLabel &&
                <AppText style={{
                    color: Colors.newDesign.darkGrey,
                    fontSize: GlobalStyleAttributes.fonts.newBadge,
                    textTransform: 'uppercase',
                }}>{groupLabel}</AppText>
            }
            {cards.map((templateId, index) => (
                <AutoMessages
                    key={templateId ?? 'default'}
                    messageType={messageType}
                    header={header}
                    subheader={subheader}
                    periodAmountTextBefore={periodAmountTextBefore}
                    periodAmountTextAfter={periodAmountTextAfter}
                    sessionType={sessionType}
                    cardRows={templateId
                        ? groupRows.filter(item => item.message_template?.id === templateId)
                        : []}
                    canDelete={index > 0 && !!templateId}
                    onDelete={setDeleteTemplateId}
                />
            ))}
            <CustomButtonV2
                onClick={openNewCardModal}
                override={({isHovered}) =>
                    <HStack
                        w={'100%'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        borderWidth={'1px'}
                        borderStyle={'dashed'}
                        borderColor={isHovered ? Colors.newDesign.mainBlue : Colors.borderColor}
                        borderRadius={'5px'}
                        padding={'12px'}
                    >
                        <AppText color={Colors.newDesign.mainBlue} fontSize={GlobalStyleAttributes.fonts.standard16}>
                            {`+ ${t('add-message')}`}
                        </AppText>
                    </HStack>
                }
            />
            {!!deleteTemplateId &&
                <ConfirmModal
                    isOpen={!!deleteTemplateId}
                    notAnError
                    header={t('areYouSure')}
                    subheader={t('delete-auto-message-confirm')}
                    confirmText={t('delete')}
                    cancelText={t('cancel')}
                    onConfirm={deleteCard}
                    onClose={() => setDeleteTemplateId(null)}
                />
            }
        </VStack>
    );
};

export default AutoMessagesGroup;
